import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import SourceRoundedIcon from '@mui/icons-material/SourceRounded';
import PageHeader from '../../components/PageHeader';
import DataState from '../../components/DataState';
import { useSession } from '../../permissions/SessionContext';
import { colors, layout } from '../../design-system/tokens';
import { formatDateTime, formatNumber } from './modernwash/format';
import {
  fetchJeonjuWashReportDaily,
  fetchJeonjuWashReportRange,
} from '../../api/modernwash';

/** 브라우저 로컬 날짜를 YYYY-MM-DD로 표시한다. */
const dateValue = (date) => {
  const offset = date.getTimezoneOffset() * 60_000;
  return new Date(date.getTime() - offset).toISOString().slice(0, 10);
};

const today = () => dateValue(new Date());
const daysAgo = (days) => {
  const date = new Date();
  date.setDate(date.getDate() - days);
  return dateValue(date);
};

const getScopedParams = (session) =>
  session?.user?.role === 'master' && session?.store?.id ? { storeId: session.store.id } : {};

const firstValue = (...values) => values.find((value) => value !== undefined && value !== null && value !== '');

/**
 * 백엔드의 최종 봉투와 전환 중의 얕은 응답을 함께 읽는다.
 * 원천 계약은 daily { date, rows }, range { start, end, rows, sum }이다.
 *
 * 이 화면의 정상 원천은 외주 jj001이 아니라 Mogoo에 보관한 snapshot이다.
 * `originalSourceUrl`은 운영 진단용 값일 뿐 화면에 URL을 노출하거나 외주를
 * 다시 조회하게 하지 않는다.
 */
export const normalizeJj001WashReport = (envelope, type) => {
  const wrapped = envelope?.data || {};
  const report =
    (Array.isArray(wrapped?.rows) && wrapped) ||
    wrapped?.[type] ||
    envelope?.[type] ||
    (Array.isArray(envelope?.rows) && envelope) ||
    {};
  const provenance = firstValue(envelope?.provenance, wrapped?.provenance, report?.provenance) || {};
  const source = envelope?.source || wrapped?.source || report?.source || {};
  const available = envelope?.ready !== false && envelope?.available !== false && wrapped?.available !== false;
  const snapshotCapturedAt = firstValue(
    provenance?.snapshotCapturedAt,
    provenance?.lastReceivedAt,
    wrapped?.snapshotCapturedAt,
    envelope?.snapshotCapturedAt,
    source?.snapshotCapturedAt,
    source?.lastReceivedAt,
    source?.fetchedAt,
  );
  const sourceSystem = firstValue(provenance?.system, source?.system);

  return {
    available,
    unavailableReason: firstValue(envelope?.reason, envelope?.unavailableReason, wrapped?.unavailableReason),
    rows: Array.isArray(report?.rows) ? report.rows : [],
    date: firstValue(report?.date, wrapped?.date, envelope?.date),
    start: firstValue(report?.start, wrapped?.start, envelope?.start),
    end: firstValue(report?.end, wrapped?.end, envelope?.end),
    sum: report?.sum || wrapped?.sum || envelope?.sum || null,
    sourceSystem,
    sourceLabel: '모구 보관본',
    snapshotCapturedAt,
    // 응답 계약에 originalSourceUrl이 있어도 외주 주소 자체를 노출하지 않는다.
    // 종료 예정인 외주 서비스가 현재 조회되는 것처럼 보이지 않게 과거 원천으로만 표기한다.
    originalSourceLabel: '이전 데이터 원천: jj001 외주 세차 집계',
    archiveMissing: available === false && (!sourceSystem || sourceSystem === 'mogoo_jj001_wash_archive'),
  };
};

const CountCell = ({ value }) => (
  <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
    {formatNumber(value)}
  </TableCell>
);

const ReportSource = ({ report }) => (
  <Alert severity="info" icon={<SourceRoundedIcon />} sx={{ alignItems: 'center' }}>
    <Typography component="span" variant="body2" sx={{ fontWeight: 800 }}>
      {report.sourceLabel}
    </Typography>
    {' · 최신 보관 수집 시각 '}
    {formatDateTime(report.snapshotCapturedAt)}
    <br />
    {report.originalSourceLabel}에서 보관 수집한 과거 집계입니다. 실시간 조회가 아니며, 회원·결제 상세 또는 세차 승인 판단을 대체하지 않습니다. 여기서 원천 데이터를 변경하거나 자동 귀속하지 않습니다.
  </Alert>
);

const Unavailable = ({ message, archiveMissing = false }) => (
  <Alert severity="info">
    <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
      운영 설정 후 제공
    </Typography>
    {archiveMissing
      ? '아직 모구 보관본을 수집하지 않았습니다. 보관 수집이 완료되면 이 화면에서 과거 집계를 조회할 수 있어요.'
      : message || '모구 보관본 운영 설정이 완료되면 이 화면에서 과거 집계를 조회할 수 있어요.'}
  </Alert>
);

const Jj001WashReports = () => {
  const { session } = useSession();
  const [dailyDate, setDailyDate] = useState(today);
  const [rangeStart, setRangeStart] = useState(() => daysAgo(6));
  const [rangeEnd, setRangeEnd] = useState(today);
  const [daily, setDaily] = useState(null);
  const [range, setRange] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const scopedParams = useMemo(() => getScopedParams(session), [session]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [dailyResult, rangeResult] = await Promise.all([
        fetchJeonjuWashReportDaily({ ...scopedParams, date: dailyDate }),
        fetchJeonjuWashReportRange({ ...scopedParams, start: rangeStart, end: rangeEnd }),
      ]);
      setDaily(normalizeJj001WashReport(dailyResult, 'daily'));
      setRange(normalizeJj001WashReport(rangeResult, 'range'));
    } catch (cause) {
      setError(cause?.response?.data?.message || '세차 현황을 불러오지 못했어요. 잠시 후 다시 시도해 주세요.');
    } finally {
      setLoading(false);
    }
  }, [dailyDate, rangeEnd, rangeStart, scopedParams]);

  useEffect(() => {
    load();
  }, [load]);

  const selectedDateLabel = daily?.date || dailyDate;
  const selectedRangeLabel = `${range?.start || rangeStart} ~ ${range?.end || rangeEnd}`;
  const dailySum = daily?.rows?.reduce(
    (total, row) => ({
      member: total.member + Number(row?.member || 0),
      normal: total.normal + Number(row?.normal || 0),
      total: total.total + Number(row?.total || 0),
    }),
    { member: 0, normal: 0, total: 0 },
  );
  const rangeSum = range?.sum || range?.rows?.reduce(
    (total, row) => ({
      member: total.member + Number(row?.member || 0),
      normal: total.normal + Number(row?.normal || 0),
      total: total.total + Number(row?.total || 0),
    }),
    { member: 0, normal: 0, total: 0 },
  );

  return (
    <Box>
      <Stack
        direction="row"
        spacing={2}
        sx={{ justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', rowGap: 1 }}
      >
        <PageHeader
          title="전주점 세차 보관 현황"
          description="모구 보관본의 일일·기간별 세차 집계를 읽기 전용으로 확인합니다. 외주 jj001 실시간 조회 화면이 아닙니다."
        />
        <Button variant="outlined" startIcon={<RefreshRoundedIcon />} onClick={load} disabled={loading}>
          보관본 다시 불러오기
        </Button>
      </Stack>

      <Stack spacing={layout.cardGap} sx={{ mt: 2 }}>
        <DataState loading={loading} error={error} onRetry={load}>
          {daily?.available === false && range?.available === false ? (
            <Unavailable
              message={daily?.unavailableReason || range?.unavailableReason}
              archiveMissing={daily?.archiveMissing || range?.archiveMissing}
            />
          ) : (
            <Stack spacing={layout.cardGap}>
              {(daily?.available !== false && daily) || (range?.available !== false && range) ? (
                <ReportSource report={daily?.available !== false ? daily : range} />
              ) : null}

              <Paper sx={{ p: { xs: 2, sm: layout.cardPadding } }}>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} sx={{ alignItems: { md: 'center' } }}>
                  <Typography variant="subtitle2" sx={{ minWidth: 118 }}>
                    일일 세차 현황
                  </Typography>
                  <TextField
                    label="조회일"
                    type="date"
                    size="small"
                    value={dailyDate}
                    onChange={(event) => setDailyDate(event.target.value)}
                    slotProps={{
                      inputLabel: { shrink: true },
                      htmlInput: { 'aria-label': '일일 세차 현황 조회일' },
                    }}
                  />
                  <Typography variant="caption" sx={{ color: colors.text.muted }}>
                    세차기별 회원·일반·합계 보관 집계
                  </Typography>
                </Stack>
              </Paper>

              {daily?.available === false ? (
                <Unavailable message={daily.unavailableReason} archiveMissing={daily.archiveMissing} />
              ) : (
                <DataState empty={daily?.rows?.length === 0} emptyText="선택한 날짜의 모구 세차 보관 집계가 없어요.">
                  <TableContainer component={Paper}>
                    <Table size="small" sx={{ minWidth: 620 }} aria-label="jj001 일일 세차 현황">
                      <TableHead>
                        <TableRow>
                          <TableCell>세차기</TableCell>
                          <TableCell align="right">회원</TableCell>
                          <TableCell align="right">일반</TableCell>
                          <TableCell align="right">합계</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {daily?.rows?.map((row, index) => (
                          <TableRow key={`${row?.location || 'washer'}-${index}`}>
                            <TableCell>{row?.location || '-'}</TableCell>
                            <CountCell value={row?.member} />
                            <CountCell value={row?.normal} />
                            <CountCell value={row?.total} />
                          </TableRow>
                        ))}
                        <TableRow sx={{ '& td': { fontWeight: 800, bgcolor: colors.surfaceSubtle } }}>
                          <TableCell>{selectedDateLabel} 합계</TableCell>
                          <CountCell value={dailySum?.member} />
                          <CountCell value={dailySum?.normal} />
                          <CountCell value={dailySum?.total} />
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </DataState>
              )}

              <Paper sx={{ p: { xs: 2, sm: layout.cardPadding } }}>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} sx={{ alignItems: { md: 'center' }, flexWrap: 'wrap' }}>
                  <Typography variant="subtitle2" sx={{ minWidth: 118 }}>
                    기간 세차 현황
                  </Typography>
                  <TextField
                    label="시작일"
                    type="date"
                    size="small"
                    value={rangeStart}
                    onChange={(event) => setRangeStart(event.target.value)}
                    slotProps={{
                      inputLabel: { shrink: true },
                      htmlInput: { 'aria-label': '기간 세차 현황 시작일' },
                    }}
                  />
                  <TextField
                    label="종료일"
                    type="date"
                    size="small"
                    value={rangeEnd}
                    onChange={(event) => setRangeEnd(event.target.value)}
                    slotProps={{
                      inputLabel: { shrink: true },
                      htmlInput: { 'aria-label': '기간 세차 현황 종료일' },
                    }}
                  />
                  <Typography variant="caption" sx={{ color: colors.text.muted }}>
                    일자별 회원·일반·합계 보관 집계
                  </Typography>
                </Stack>
              </Paper>

              {range?.available === false ? (
                <Unavailable message={range.unavailableReason} archiveMissing={range.archiveMissing} />
              ) : (
                <DataState empty={range?.rows?.length === 0} emptyText="선택한 기간의 모구 세차 보관 집계가 없어요.">
                  <TableContainer component={Paper}>
                    <Table size="small" sx={{ minWidth: 620 }} aria-label="jj001 기간 세차 현황">
                      <TableHead>
                        <TableRow>
                          <TableCell>일자</TableCell>
                          <TableCell align="right">회원</TableCell>
                          <TableCell align="right">일반</TableCell>
                          <TableCell align="right">합계</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {range?.rows?.map((row, index) => (
                          <TableRow key={`${row?.date || 'date'}-${index}`}>
                            <TableCell>{row?.date || '-'}</TableCell>
                            <CountCell value={row?.member} />
                            <CountCell value={row?.normal} />
                            <CountCell value={row?.total} />
                          </TableRow>
                        ))}
                        <TableRow sx={{ '& td': { fontWeight: 800, bgcolor: colors.surfaceSubtle } }}>
                          <TableCell>{selectedRangeLabel} 합계</TableCell>
                          <CountCell value={rangeSum?.member} />
                          <CountCell value={rangeSum?.normal} />
                          <CountCell value={rangeSum?.total} />
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </DataState>
              )}
            </Stack>
          )}
        </DataState>
      </Stack>
    </Box>
  );
};

export default Jj001WashReports;
